// <s-holiday-slider> — moving banner: scroll-snap track that advances on a timer.
// Pauses on hover, focus, and when the tab is hidden; no autoplay under prefers-reduced-motion.
class SHolidaySlider extends HTMLElement {
  #controller = null;
  #timer = null;
  #index = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const track = this.querySelector("[data-slider-track]");
    if (!track) return;
    this.track = track;
    this.slides = [...track.querySelectorAll("[data-slide]")];
    this.dots = [...this.querySelectorAll("[data-slider-dot]")];

    this.querySelector("[data-slider-prev]")?.addEventListener("click", () => this.#go(this.#index - 1), { signal });
    this.querySelector("[data-slider-next]")?.addEventListener("click", () => this.#go(this.#index + 1), { signal });
    this.dots.forEach((dot) => dot.addEventListener("click", () => this.#go(Number(dot.dataset.sliderDot)), { signal }));

    let scrollTick = null;
    track.addEventListener("scroll", () => {
      clearTimeout(scrollTick);
      scrollTick = setTimeout(() => this.#sync(), 80);
    }, { signal, passive: true });

    track.addEventListener("keydown", (event) => {
      if (event.key === "ArrowRight") { event.preventDefault(); this.#go(this.#index + 1); }
      if (event.key === "ArrowLeft") { event.preventDefault(); this.#go(this.#index - 1); }
    }, { signal });

    this.addEventListener("mouseenter", () => this.#stop(), { signal });
    this.addEventListener("mouseleave", () => this.#start(), { signal });
    this.addEventListener("focusin", () => this.#stop(), { signal });
    this.addEventListener("focusout", () => this.#start(), { signal });
    document.addEventListener("visibilitychange", () => (document.hidden ? this.#stop() : this.#start()), { signal });

    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    this.#controller?.abort();
  }

  #reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  #start() {
    this.#stop();
    if (this.#reducedMotion() || this.slides.length < 2) return;
    const interval = Number(this.dataset.interval) || 4000;
    this.#timer = setInterval(() => this.#go(this.#index + 1), interval);
  }

  #stop() {
    clearInterval(this.#timer);
    this.#timer = null;
  }

  #go(target) {
    const count = this.slides.length;
    if (!count) return;
    this.#index = (target + count) % count;
    this.track.scrollTo({ left: this.slides[this.#index].offsetLeft - this.track.offsetLeft, behavior: this.#reducedMotion() ? "auto" : "smooth" });
    this.#paint();
  }

  #sync() {
    const width = this.track.clientWidth || 1;
    this.#index = Math.round(this.track.scrollLeft / width);
    this.#paint();
  }

  #paint() {
    this.dots.forEach((dot, i) => dot.setAttribute("aria-current", i === this.#index ? "true" : "false"));
  }
}

if (!customElements.get("s-holiday-slider")) {
  customElements.define("s-holiday-slider", SHolidaySlider);
}
